import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wrench, Package, CheckCircle, ArrowLeftRight, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const CATEGORIES = {
  power_tools: { label: 'Power Tools', icon: '⚡' },
  hand_tools: { label: 'Hand Tools', icon: '🔧' },
  safety_equipment: { label: 'Safety Equipment', icon: '🦺' },
  measuring_tools: { label: 'Measuring Tools', icon: '📏' },
  other: { label: 'Other', icon: '📦' }
};

const MyTools = ({ user }) => {
  const [myTools, setMyTools] = useState([]);
  const [availableTools, setAvailableTools] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checkinDialogOpen, setCheckinDialogOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [checkoutNotes, setCheckoutNotes] = useState('');
  const [checkinNotes, setCheckinNotes] = useState('');
  const [checkinCondition, setCheckinCondition] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [myToolsRes, availableRes, projectsRes] = await Promise.all([
        axios.get(`${API}/tools/my-tools`),
        axios.get(`${API}/tools?status=available`),
        axios.get(`${API}/projects`)
      ]);
      setMyTools(myToolsRes.data);
      setAvailableTools(availableRes.data);
      setProjects(projectsRes.data.filter(p => p.status === 'active'));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!selectedTool || !selectedProject) {
      toast.error('Please select a tool and project');
      return;
    }
    try {
      await axios.post(`${API}/tools/${selectedTool}/checkout`, {
        project_id: selectedProject,
        notes: checkoutNotes
      });
      toast.success('Tool checked out successfully!');
      setCheckoutDialogOpen(false);
      setSelectedTool(null);
      setSelectedProject('');
      setCheckoutNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check out tool');
    }
  };

  const handleCheckin = async () => {
    if (!selectedTool) return;
    try {
      await axios.post(`${API}/tools/${selectedTool}/checkin`, {
        notes: checkinNotes,
        condition: checkinCondition || undefined
      });
      toast.success('Tool checked in successfully!');
      setCheckinDialogOpen(false);
      setSelectedTool(null);
      setCheckinNotes('');
      setCheckinCondition('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in tool');
    }
  };

  const openCheckoutDialog = (toolId = null) => {
    setSelectedTool(toolId);
    setSelectedProject('');
    setCheckoutNotes('');
    setCheckoutDialogOpen(true);
  };

  const openCheckinDialog = (tool) => {
    setSelectedTool(tool.id);
    setCheckinCondition(tool.condition);
    setCheckinNotes('');
    setCheckinDialogOpen(true);
  };

  // Only employees can access this page
  if (user.role !== 'employee' && user.role !== 'admin' && user.role !== 'supervisor') {
    return (
      <div className="min-h-screen bg-gray-50 pb-20 pt-20 flex items-center justify-center">
        <div className="text-center">
          <Wrench className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only employees can access My Tools.</p>
        </div>
      </div>
    );
  }

  const getDaysCheckedOut = (checkedOutDate) => {
    if (!checkedOutDate) return 0;
    const diff = new Date() - new Date(checkedOutDate);
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20 pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-20" data-testid="my-tools-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Wrench className="w-8 h-8 text-orange-600" />
              My Tools
            </h1>
            <p className="text-gray-600 mt-1">Manage tools you have checked out</p>
          </div>
          
          <Button onClick={() => openCheckoutDialog()} className="bg-orange-600 hover:bg-orange-700 text-white">
            <ArrowLeftRight className="w-4 h-4 mr-2" />
            Check Out Tool
          </Button>
        </div>

        {/* My Checked Out Tools */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Currently Checked Out ({myTools.length})
          </h2>
          
          {myTools.length === 0 ? (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-8 text-center">
              <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Tools Checked Out</h3>
              <p className="text-gray-600 mb-4">You don&apos;t have any tools checked out right now.</p>
              <Button onClick={() => openCheckoutDialog()} className="bg-orange-600 hover:bg-orange-700">
                Check Out a Tool
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myTools.map((tool) => {
                const daysOut = getDaysCheckedOut(tool.last_checked_out);
                const isOverdue = daysOut > 7;
                const category = CATEGORIES[tool.category] || CATEGORIES.other;
                
                return (
                  <div 
                    key={tool.id} 
                    className={`bg-white border-2 rounded-lg p-6 ${isOverdue ? 'border-red-300' : 'border-gray-200'}`}
                  >
                    {isOverdue && (
                      <div className="bg-red-50 text-red-700 text-sm font-medium px-3 py-1 rounded-full mb-3 inline-block">
                        ⚠️ Overdue ({daysOut} days)
                      </div>
                    )}
                    
                    <div className="flex items-start gap-3 mb-4">
                      <span className="text-2xl">{category.icon}</span>
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg">{tool.name}</h3>
                        <span className="text-sm text-gray-500">{category.label}</span>
                      </div>
                    </div>

                    {tool.serial_number && (
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">S/N:</span> {tool.serial_number}
                      </p>
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-2 text-sm text-blue-800 mb-1">
                        <MapPin className="w-4 h-4" />
                        <span className="font-medium">{tool.current_project_name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-blue-600">
                        <Clock className="w-3 h-3" />
                        <span>
                          Since {new Date(tool.last_checked_out).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })} ({daysOut} day{daysOut !== 1 ? 's' : ''})
                        </span>
                      </div>
                    </div>

                    <Button 
                      onClick={() => openCheckinDialog(tool)} 
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Check In Tool
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Available Tools */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Available Tools ({availableTools.length})
          </h2>
          
          {availableTools.length === 0 ? (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-8 text-center">
              <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600">No tools available for checkout at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {availableTools.map((tool) => {
                const category = CATEGORIES[tool.category] || CATEGORIES.other;
                
                return (
                  <div key={tool.id} className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xl">{category.icon}</span>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{tool.name}</h3>
                        <span className="text-xs text-gray-500">{category.label}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        tool.condition === 'good' ? 'bg-green-100 text-green-700' :
                        tool.condition === 'fair' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {tool.condition}
                      </span>
                      <Button 
                        size="sm" 
                        onClick={() => openCheckoutDialog(tool.id)}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        Check Out
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Checkout Dialog */}
        <Dialog open={checkoutDialogOpen} onOpenChange={setCheckoutDialogOpen}>
          <DialogContent className="bg-white text-gray-900 border-gray-300 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gray-900 text-xl">Check Out Tool</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-gray-700">Select Tool *</Label>
                <Select value={selectedTool || ''} onValueChange={setSelectedTool}>
                  <SelectTrigger className="text-gray-900 bg-white border-gray-300">
                    <SelectValue placeholder="Choose a tool..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTools.map(tool => (
                      <SelectItem key={tool.id} value={tool.id}>
                        {CATEGORIES[tool.category]?.icon} {tool.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-gray-700">Select Project *</Label>
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger className="text-gray-900 bg-white border-gray-300">
                    <SelectValue placeholder="Choose a project..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-gray-700">Notes (optional)</Label>
                <Input
                  value={checkoutNotes}
                  onChange={(e) => setCheckoutNotes(e.target.value)}
                  placeholder="Any notes about this checkout..."
                  className="text-gray-900 bg-white border-gray-300"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setCheckoutDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCheckout} className="bg-orange-600 hover:bg-orange-700">
                  Check Out
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Checkin Dialog */}
        <Dialog open={checkinDialogOpen} onOpenChange={setCheckinDialogOpen}>
          <DialogContent className="bg-white text-gray-900 border-gray-300 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gray-900 text-xl">Check In Tool</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-gray-700">Tool Condition</Label>
                <Select value={checkinCondition} onValueChange={setCheckinCondition}>
                  <SelectTrigger className="text-gray-900 bg-white border-gray-300">
                    <SelectValue placeholder="Select condition..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="poor">Poor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-gray-700">Notes (optional)</Label>
                <Input
                  value={checkinNotes}
                  onChange={(e) => setCheckinNotes(e.target.value)}
                  placeholder="Any notes about the tool condition..."
                  className="text-gray-900 bg-white border-gray-300"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setCheckinDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCheckin} className="bg-green-600 hover:bg-green-700">
                  Check In
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MyTools;
